import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EventService } from '../../../service/event.service';
import { SwalService } from '../../../service/swal.service';
import { SubscriptionService } from '../../../service/subscription.service';
import { ContractSettingsComponent } from '../../../components/contract-settings/contract-settings.component';
import { EventDetailStore } from '../event-detail.store';
import { IconComponent } from '../../../components/icon/icon.component';
import { shareOrCopy } from '../../../utility/share';

type ContractStage = 'draft' | 'ready' | 'sent' | 'signed' | 'done';

@Component({
  selector: 'app-contratto-tab',
  templateUrl: './contratto-tab.component.html',
  styleUrls: ['./contratto-tab.component.css'],
  imports: [CommonModule, FormsModule, TranslatePipe, ContractSettingsComponent, IconComponent],
})
export class ContrattoTabComponent implements OnInit {
  // ── STORICO ──
  showHistory = false;
  contractVersions: {
    file: string;
    label: string;
    generated_at: string;
    is_signed?: boolean;
  }[] = [];
  contractVersionsLoaded = false;

  // ── ANTEPRIMA ──
  previewOpen = false;
  previewUrl: SafeResourceUrl | null = null;
  private previewObjectUrl: string | null = null;
  private previewBlob: Blob | null = null;
  private previewSigned = false;
  private previewLang = 'it';
  finalizing = false;

  signatureLink: string | null = null;

  readonly steps = [
    'riepilogo.contract_steps.generate',
    'riepilogo.contract_steps.send',
    'riepilogo.contract_steps.sign',
    'riepilogo.contract_steps.signed_pdf',
  ];
  customOpen = false;
  customEverOpened = false;

  constructor(
    public store: EventDetailStore,
    private ms: EventService,
    private swal: SwalService,
    private sanitizer: DomSanitizer,
    private subscription: SubscriptionService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.subscription.loadEntitlements();
    if (this.store.autoPreview && !this.store.isCollaborator) {
      this.store.autoPreview = false;
      this.startPreviewWhenIdle();
    }
  }

  // Ogni richiesta col loader sostituisce e poi chiude la Swal aperta: la scelta
  // lingua parte solo quando la scheda ha finito di caricare (due controlli liberi).
  private startPreviewWhenIdle(tries = 0, idle = 0): void {
    if (tries > 60) return;
    setTimeout(() => {
      if (this.swal.isOpen()) {
        this.startPreviewWhenIdle(tries + 1, 0);
      } else if (idle >= 1) {
        this.generaContratto();
      } else {
        this.startPreviewWhenIdle(tries + 1, idle + 1);
      }
    }, 250);
  }

  get event(): any { return this.store.event; }
  get matrimonio(): any { return this.store.event; }
  get editMode(): boolean { return this.store.editMode; }
  get isCollaborator(): boolean { return this.store.isCollaborator; }
  get clientFullName(): string { return this.store.clientFullName; }
  canSee(section: string): boolean { return this.store.canSee(section); }

  get eventId(): string {
    return this.store.eventId!;
  }

  get hasContract(): boolean {
    return !!this.event?.event?.contract?.file;
  }

  // ── PERCORSO A TAPPE ──
  get activeSignatureLink(): string | null {
    if (this.signatureLink) return this.signatureLink;
    const sr = this.event?.signature_request;
    if (!sr?.token) return null;
    if (sr.expires_at && new Date(sr.expires_at) <= new Date()) return null;
    return `${window.location.origin}/f/${sr.token}`;
  }

  get stage(): ContractStage {
    if (!this.hasContract) return 'draft';
    const sr = this.event?.signature_request;
    if (this.event?.event?.contract?.is_signed || sr?.signed_contract_file) return 'done';
    if (sr?.signed_at) return 'signed';
    if (this.activeSignatureLink) return 'sent';
    return 'ready';
  }

  get stepIndex(): number {
    const order: Record<ContractStage, number> = { draft: 0, ready: 1, sent: 2, signed: 3, done: 4 };
    return order[this.stage];
  }

  get stageHintParams(): Record<string, string> {
    const sr = this.event?.signature_request;
    const fmt = (d: any) => (d ? new Date(d).toLocaleDateString() : '');
    return { expires: fmt(sr?.expires_at), signed: fmt(sr?.signed_at) };
  }

  toggleCustom() {
    this.customOpen = !this.customOpen;
    if (this.customOpen) this.customEverOpened = true;
  }

  // ── AZIONI CONTRATTO ──
  scaricaContratto() {
    this.ms.scaricaContratto(this.eventId);
  }

  visualizzaContratto() {
    this.ms.visualizzaContratto(this.eventId);
  }

  loadContractVersions() {
    if (this.contractVersionsLoaded) return;
    this.ms.getContractVersions(
      this.eventId,
      (data) => {
        this.contractVersions = data.versions ?? [];
        this.contractVersionsLoaded = true;
      },
      () => this.swal.error('Errore nel caricamento dello storico'),
    );
  }

  scaricaVersione(versionFile: string) {
    this.ms.scaricaContractVersion(this.eventId, versionFile);
  }

  async generaContratto() {
    if (!this.event?.event?.date) {
      this.swal.error(this.translate.instant('crea.period.date_needed'));
      return;
    }
    if (this.stage === 'signed' || this.stage === 'done') {
      const res = await this.swal.confirm(
        this.translate.instant('riepilogo.regenerate_signed_title'),
        this.translate.instant('riepilogo.regenerate_signed_text'),
        this.translate.instant('riepilogo.regenerate'),
      );
      if (!res.isConfirmed) return;
    }
    // Gate proattivo: l'anteprima genera il PDF (scaricabile), quindi va
    // bloccata a monte per i non abbonati, non solo la finalizzazione.
    this.subscription.ensureLoaded(() => {
      if (!this.subscription.canBase()) {
        this.subscription.promptUpgrade(
          'Genera il contratto con un abbonamento attivo',
        );
        return;
      }
      this.ms.buildContratto(this.eventId, (blob, ctx) => {
        this.setPreview(blob, ctx.signed, ctx.lang);
      });
    });
  }

  private setPreview(blob: Blob, signed: boolean, lang: string) {
    this.clearPreviewUrl();
    this.previewBlob = blob;
    this.previewSigned = signed;
    this.previewLang = lang;
    this.previewObjectUrl = URL.createObjectURL(blob);
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.previewObjectUrl,
    );
    this.previewOpen = true;
  }

  finalizzaContratto() {
    if (!this.previewBlob) return;
    this.finalizing = true;
    this.ms.finalizeContratto(
      this.eventId,
      this.previewLang,
      this.previewSigned,
      (res: any) => {
        this.finalizing = false;
        if (!this.event.event.contract) this.event.event.contract = {};
        this.event.event.contract.file = res?.file ?? true;
        this.event.event.contract.is_signed = this.previewSigned;
        this.event.event.contract.generateContract = true;
        // Contratto nuovo: il backend azzera la firma raccolta sul precedente
        if (!this.previewSigned && this.event.signature_request?.signed_at) {
          this.event.signature_request = {};
          this.signatureLink = null;
        }
        this.contractVersionsLoaded = false;
        this.loadContractVersions();
        this.closePreview();
        this.swal.success('Contratto generato!');
      },
      () => {
        this.finalizing = false;
        this.swal.errorDialog('Errore', 'Generazione contratto fallita');
      },
    );
  }

  closePreview() {
    this.previewOpen = false;
    this.clearPreviewUrl();
    this.previewBlob = null;
  }

  private clearPreviewUrl() {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.previewUrl = null;
  }

  onContractOverrideSaved(override: Record<string, any> | null): void {
    if (this.event) this.event.contract_override = override;
  }

  onDepositSaved(deposit: { cost: number; type: 'fixed' | 'percent' }): void {
    if (!this.event.financial) this.event.financial = {};
    this.event.financial.deposit = { ...this.event.financial.deposit, ...deposit };
  }

  // ── FIRMA ──
  sendForSignature() {
    this.ms.generateSignatureLink(
      this.eventId,
      (data: any) => {
        const base = window.location.origin;
        this.signatureLink = `${base}/f/${data.link}`;
        this.event.signature_request = {
          ...(this.event.signature_request ?? {}),
          token: data.link,
          expires_at: data.expires_at,
          signed_at: null,
          signed_contract_file: null,
        };
        this.shareSignatureLink();
      },
      (err: any) => this.swal.errorDialog(
        this.translate.instant('swal.error'),
        err?.error?.message || this.translate.instant('riepilogo.signature_link_error'),
      ),
    );
  }

  async shareSignatureLink() {
    const url = this.activeSignatureLink;
    if (!url) return;
    const outcome = await shareOrCopy({
      title: this.translate.instant('riepilogo.signature_share_title'),
      text: this.translate.instant('riepilogo.signature_share_text', { name: this.clientFullName }),
      url,
    });
    if (outcome === 'copied') this.swal.success(this.translate.instant('riepilogo.hero.copied'));
  }

  async generaPdfFirmato() {
    const brideSignature = await this.ms.prepareBrideSignature(this.eventId);
    if (!brideSignature) {
      this.swal.errorDialog('Errore', 'Firma non trovata');
      return;
    }

    this.ms.generaEsalvaContratto(
      this.eventId,
      () => {
        if (!this.event.signature_request) {
          this.event.signature_request = {};
        }
        this.event.signature_request.signed_contract_file = true;
      },
      true,
    );
  }
}
